import {
  Point,
  Range,
  Path,
  Node,
  Editor,
  Text,
  Transforms,
} from 'domains/reporter/RichTextEditor/core';
import equals from 'ramda/es/equals';
import { find } from '../../utils/find';
import type { NodeEntry } from '../../core';
import { DEEP_LINK_PLUGIN_ID } from './types';
import type { DeepLinkPluginElement, DeepLink } from './types';
import { unreachableCaseError } from 'types';
import {
  DEFAULT_STUDY_DATE_VALUE,
  MISSING_STUDY_DATE,
  MISSING_STUDY_DESCRIPTION,
  DeepLinkVariants,
  createEmptyTextNode,
  createDelimiterTextNode,
} from './constants';
import { INLINE_BOOKMARK_PLUGIN_ID } from '../inlineBookmark/types';
import { PageTypes } from 'utils/pageTypes';
import { getComparisonHeading, getNextHeadingOrHeadingLikeNode } from '../heading/utils';
import { compareAsc } from 'date-fns/compareAsc';
import { compareDesc } from 'date-fns/compareDesc';
import { toLocaleString } from 'utils/date';
import type { HeadingKeywords } from 'generated/graphql';
import type { ReviewedStudyByDate } from 'domains/viewer/ViewportDre/modules/imaging/useTrackViewedSlices';
import { Key, ReactEditor } from 'slate-react';
import { logger } from 'modules/logger';
import {
  createInlineBookmark,
  getFirstInlineBookmarkInPath,
  getInlineBookmarkInSameParagraph,
} from '../inlineBookmark';
import {
  createInlineParagraphWithChildren,
  getParentParagraph,
} from 'domains/reporter/RichTextEditor/plugins/paragraph';
import { getNextInsertionPoint } from 'domains/reporter/RichTextEditor/utils/getNextInsertionPoint';

const getTextNode = (node: DeepLink, associatedStudy?: ReviewedStudyByDate) => {
  switch (node.variant) {
    case DeepLinkVariants.IMAGE_SLICE:
      return {
        text:
          node.context.seriesDisplayName != null
            ? `(series ${node.context.seriesDisplayName}, image ${node.context.imageNumber})`
            : `(image ${node.context.imageNumber})`,
      };
    case DeepLinkVariants.POINT:
      return {
        text: node.context.label,
      };
    case DeepLinkVariants.STUDY: {
      if (associatedStudy == null) break;

      const { studyDate, description } = associatedStudy;
      const formattedDate = studyDate != null ? toLocaleString(studyDate) : MISSING_STUDY_DATE;
      const label = `${description ?? MISSING_STUDY_DESCRIPTION} ${formattedDate ?? MISSING_STUDY_DATE}`;
      return {
        text: label,
      };
    }
    default:
      unreachableCaseError((node as DeepLinkPluginElement).variant);
  }
};

/**
 * Insert a node where ever the current selection is in the editor.
 * @param {Editor} editor The editor to insert into.
 */
export const insertDeepLink = ({
  editor,
  at,
  node,
}: {
  editor: Editor;
  node: DeepLink;
  at?: Path | Point | Range | undefined;
}) => {
  //If this is an image slice variant - use that context to generate the text?
  Transforms.insertNodes(
    editor,
    {
      type: DEEP_LINK_PLUGIN_ID,
      ...node,
      children: [getTextNode(node)],
    },
    {
      at,
    }
  );
};

export const generateComparisonDeepLinkNode = (study: ReviewedStudyByDate): DeepLink => {
  return {
    target: PageTypes.VIEWER,
    variant: DeepLinkVariants.STUDY,
    context: {
      studySmid: study.smid,
    },
  };
};

export const isStudyDeepLink = (node: Node | DeepLink): boolean =>
  node != null &&
  node.variant === DeepLinkVariants.STUDY &&
  node.type === DEEP_LINK_PLUGIN_ID &&
  node.context?.studySmid != null &&
  typeof node.context.studySmid === 'string';

export const isOlderDeepLink = (
  node: Node | DeepLink,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  studyDate?: Date | number | null
): boolean => {
  if (node == null || isStudyDeepLink(node) === false) return false;
  const found = reviewedStudies.get(node.context.studySmid);
  if (found == null) return false;
  return (
    compareDesc(
      new Date(found.studyDate ?? DEFAULT_STUDY_DATE_VALUE),
      new Date(studyDate ?? DEFAULT_STUDY_DATE_VALUE)
    ) > 0
  );
};

export const isNewerDeepLink = (
  node: Node | DeepLink,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  studyDate?: Date | number | null
): boolean => {
  if (node == null || isStudyDeepLink(node) === false) return false;
  const found = reviewedStudies.get(node.context.studySmid);
  if (found == null) return false;
  return (
    compareAsc(
      new Date(found.studyDate ?? DEFAULT_STUDY_DATE_VALUE),
      new Date(studyDate ?? DEFAULT_STUDY_DATE_VALUE)
    ) > 0
  );
};

export const createDeepLinkWrapper = (deepLink: DeepLink): DeepLinkPluginElement => {
  return {
    type: DEEP_LINK_PLUGIN_ID,
    ...deepLink,
    children: [],
  };
};

/**
 * Find a node in the editor by its guid
 * @param {Editor} editor The editor to search in
 */
export const findDeepLink = ({
  editor,
  node,
}: {
  editor: Editor;
  node: DeepLink;
}): Nullable<NodeEntry<DeepLinkPluginElement>> =>
  find<DeepLinkPluginElement>(
    editor,
    (n: Node) =>
      n.type === DEEP_LINK_PLUGIN_ID &&
      n.variant === node.variant &&
      n.target === node.target &&
      equals((n as DeepLinkPluginElement).context, node.context),
    { at: [] }
  );

export const isValidDeepLink: (payload: DeepLink) => boolean = (payload) => {
  return (
    ['viewer', 'worklist'].includes(payload.target) &&
    ['imageSlice', 'point', 'study'].includes(payload.variant)
  );
};

export const getExistingBookmarkWithStudyDeepLinks = (
  editor: Editor,
  start: Path = [],
  end: Path = []
): NodeEntry<DeepLinkPluginElement | Node> | void | null => {
  const maybeDeepLinkStudyNodes = Editor.nodes(editor, {
    at: [start, end],
    match: isStudyDeepLink,
  }).next();
  if (maybeDeepLinkStudyNodes.done) {
    return null;
  }
  const maybeComparisonFieldWithDeepLinks = Editor.above(editor, {
    at: maybeDeepLinkStudyNodes.value[1],
    mode: 'lowest',
    match: (node: Node) => node.type === INLINE_BOOKMARK_PLUGIN_ID,
  });
  if (maybeComparisonFieldWithDeepLinks == null) {
    return null;
  }
  return maybeComparisonFieldWithDeepLinks;
};

const getEligibleReviewedStudies = ({
  reviewedStudies,
  currentStudySmid,
  currentComparativeStudySmids,
  allowStaleComparatives,
  excludedSmids,
}: {
  reviewedStudies: Map<string, ReviewedStudyByDate>;
  currentStudySmid?: string | null;
  currentComparativeStudySmids?: readonly string[] | null;
  allowStaleComparatives: boolean;
  excludedSmids?: ReadonlySet<string>;
}): Array<[string, ReviewedStudyByDate]> =>
  Array.from(reviewedStudies).filter(([smid]) => {
    // When the study in question is the primary study, do not add it.
    // TODO: This should be handled upstream by the logic that is providing the reviewed studies.
    //       If that is fixed, we could remove this check.
    if (smid === currentStudySmid) {
      logger.warn(
        '[populateComparisonStudyNodes] Skipping insertion of the primary study into the comparison field'
      );
      return false;
    }

    // Linked studies are current exams and must never appear in the comparison field.
    if (excludedSmids?.has(smid) === true) {
      return false;
    }

    // When the study in question is not a current comparative study, do not add it.
    // This may occur for example if the comparative study was previously added, but the viewer is not open,
    // or the study was removed from the comparisons.
    if (!allowStaleComparatives && !(currentComparativeStudySmids ?? []).includes(smid)) {
      logger.info(
        `[populateComparisonStudyNodes] Not inserting non-active comparative study ${smid}`
      );
      return false;
    }

    return true;
  });

export type PopulateComparisonStudyNodesOptions = {
  allowStaleComparatives?: boolean;
  replaceExistingBookmarkContent?: boolean;
  excludedSmids?: ReadonlySet<string>;
};

export const populateComparisonStudyNodes = (
  editor: Nullable<Editor>,
  reviewedStudies: Map<string, ReviewedStudyByDate>,
  comparisonHeadingKeywords: HeadingKeywords['comparison'],
  currentStudySmid?: string | null,
  currentComparativeStudySmids?: readonly string[] | null,
  options?: PopulateComparisonStudyNodesOptions
): void => {
  const allowStaleComparatives = options?.allowStaleComparatives ?? false;
  const replaceExistingBookmarkContent = options?.replaceExistingBookmarkContent ?? false;
  if (
    editor == null ||
    reviewedStudies == null ||
    reviewedStudies.size < 1 ||
    comparisonHeadingKeywords == null ||
    comparisonHeadingKeywords.length < 1
  )
    return;

  // Don't auto-fill if we can't find a comparison heading or heading-like node
  const comparisonHeading = getComparisonHeading(editor, comparisonHeadingKeywords);
  if (comparisonHeading == null) {
    logger.info('[populateComparisonStudyNodes] No comparison heading found');
    return;
  }

  const eligibleReviewedStudies = getEligibleReviewedStudies({
    reviewedStudies,
    currentStudySmid,
    currentComparativeStudySmids,
    allowStaleComparatives,
    excludedSmids: options?.excludedSmids,
  });

  /**
   * Target logic (find the target bookmark to insert into)
   */
  // Get the next heading
  const nextHeading = getNextHeadingOrHeadingLikeNode({ editor, location: comparisonHeading[1] });
  const comparisonSectionEndPath = nextHeading?.[1] ?? Editor.last(editor, [])[1];

  // Look for existing fields with study deep links
  const existingBookmarkWithStudyDeepLinks = getExistingBookmarkWithStudyDeepLinks(
    editor,
    comparisonHeading[1],
    comparisonSectionEndPath
  );
  let targetBookmark = existingBookmarkWithStudyDeepLinks;

  // If there are no existing study deep links, get the first inline bookmark.
  // Preserve non-empty content unless the caller is replacing template/default text.
  if (targetBookmark == null) {
    if (eligibleReviewedStudies.length === 0) {
      return;
    }

    // A heading-like paragraph can contain both the label and the target field, e.g.
    // "COMPARISON: [None]". Prefer that inline bookmark so default text is cleared
    // instead of inserting into a later bookmark in the same section.
    const firstInlineBookmark =
      getInlineBookmarkInSameParagraph(editor, comparisonHeading[1]) ??
      getFirstInlineBookmarkInPath(editor, comparisonHeading[1], comparisonSectionEndPath);
    const hasExistingBookmarkContent =
      firstInlineBookmark != null && Node.string(firstInlineBookmark[0]).trim() !== '';

    if (hasExistingBookmarkContent && !replaceExistingBookmarkContent) {
      logger.info('[populateComparisonStudyNodes] Preserving existing comparison bookmark content');
      return;
    }

    Editor.withoutNormalizing(editor, () => {
      const nextInsertionPoint = getNextInsertionPoint(editor, comparisonHeading[1]);
      let bookmarkParentParagraph = null;
      if (firstInlineBookmark != null) {
        bookmarkParentParagraph = getParentParagraph(editor, firstInlineBookmark[1]);
      }
      // If there are no paragraphs with any bookmarks, create one
      if (bookmarkParentParagraph == null) {
        const emptyInlineBookmark = createInlineParagraphWithChildren([createInlineBookmark()]);
        Transforms.insertNodes(editor, emptyInlineBookmark, {
          at: nextInsertionPoint,
        });
      } else if (firstInlineBookmark != null) {
        // Wipe out the existing inline bookmark
        // First, remove the inline bookmark node entirely.
        Transforms.removeNodes(editor, { at: firstInlineBookmark[1] });
        // Then, replace it with a clone that has no children.
        Transforms.insertNodes(
          editor,
          { ...firstInlineBookmark[0], children: [{ text: '' }] },
          { at: firstInlineBookmark[1] }
        );
      }
    });
    // Set the target bookmark to the first inline bookmark we just created
    targetBookmark = Editor.next(editor, {
      at: comparisonHeading[1],
      match: (node: Node) => node.type === INLINE_BOOKMARK_PLUGIN_ID,
    });
  }

  if (targetBookmark == null) {
    logger.warn('[populateComparisonStudyNodes] Unable to locate target bookmark for comparison');
    return;
  }

  const firstNode = Editor.first(editor, targetBookmark[1]);
  if (firstNode == null) return;
  const firstNodePath = Editor.before(editor, firstNode[1]);
  if (firstNodePath == null) return;
  const lastNode = Editor.last(editor, targetBookmark[1]);
  if (lastNode == null) return;
  const lastNodePath = Editor.after(editor, lastNode[1]);
  if (lastNodePath == null) return;

  const allDeepLinkNodes = Array.from(
    Editor.nodes(editor, { at: targetBookmark[1], match: isStudyDeepLink }) ?? []
  );

  /**
   * Removal logic (remove all deep link nodes that are not in the reviewed studies)
   */
  const nodePathsToRemove: Array<Path> = [];
  for (let i = 0; i < allDeepLinkNodes.length; i++) {
    const [dlNode, dlNodePath] = allDeepLinkNodes[i];
    if (!reviewedStudies.has(dlNode.context.studySmid)) {
      nodePathsToRemove.push(dlNodePath);
      // remove associated text nodes
      const associatedText = Editor.next(editor, {
        at: dlNodePath,
        match: (node: Node) => Text.isText(node),
      });
      if (associatedText != null) {
        if (i === allDeepLinkNodes.length - 1) {
          // clear the text node if it is associated with the last deep link node
          // in the comparison field to be removed
          Transforms.setNodes(editor, createEmptyTextNode(), { at: associatedText[1] });
        } else {
          nodePathsToRemove.push(associatedText[1]);
        }
      }
    }
  }
  while (nodePathsToRemove.length > 0) {
    const path = nodePathsToRemove.pop();
    if (path == null) continue;
    Transforms.removeNodes(editor, { at: path });
  }

  /**
   * Insertion logic (insert reviewed studies missing in the comparison field)
   */
  for (const [smid, study] of eligibleReviewedStudies) {
    // check if the study is already in the comparison field
    const sameStudy = Editor.next(editor, {
      at: firstNodePath,
      match: (node: Node | DeepLink) => {
        if (!isStudyDeepLink(node)) return false;
        return smid === node.context.studySmid;
      },
    });
    if (sameStudy != null) {
      continue;
    }

    const deepLinkNode = generateComparisonDeepLinkNode(study);
    const nodeToInsert = {
      type: DEEP_LINK_PLUGIN_ID,
      ...deepLinkNode,
      children: [getTextNode(deepLinkNode, study)],
    } as const;

    // find the next deep link node that is older than the current item
    const nextDeepLinkNode = Editor.next(editor, {
      at: firstNodePath,
      match: (node: Node) => isOlderDeepLink(node, reviewedStudies, study.studyDate),
    });
    // find the previous deep link node that is newer than the current item
    const prevDeepLinkNode = Editor.previous(editor, {
      at: lastNodePath,
      match: (node: Node) => isNewerDeepLink(node, reviewedStudies, study.studyDate),
    });

    // if inserting as oldest one in the comparison field (last node)
    if (nextDeepLinkNode == null) {
      const textNode = Editor.previous(editor, {
        at: lastNodePath,
        match: (node: Node) => Text.isText(node),
      });
      if (textNode == null) continue;

      const targetPath = textNode[1];
      targetPath[targetPath.length - 1] += 1;
      const hasTrailingComma = textNode[0].text.trim().endsWith(',');
      if (prevDeepLinkNode == null || hasTrailingComma) {
        // if there are no deep link nodes in the comparison field or the previous text node ends with a comma
        Transforms.insertNodes(editor, [nodeToInsert, createEmptyTextNode()], { at: targetPath });
      } else {
        // if the previous text node does not end with a comma, insert a comma
        Transforms.insertNodes(
          editor,
          [createDelimiterTextNode(), nodeToInsert, createEmptyTextNode()],
          {
            at: targetPath,
          }
        );
      }
      continue;
    }

    // if inserting into other positions in the comparison field
    const textNode = Editor.previous(editor, {
      at: nextDeepLinkNode[1],
      match: (node: Node) => Text.isText(node),
    });
    if (textNode == null) continue;

    const targetPath = textNode[1];
    targetPath[targetPath.length - 1] += 1;
    Transforms.insertNodes(editor, [nodeToInsert, createDelimiterTextNode()], {
      at: targetPath,
    });
  }
};

export const getDeepLinkKey = (
  editor: Editor | null,
  element: DeepLinkPluginElement | null
): Key | null => {
  if (editor == null || element == null) return null;
  try {
    return ReactEditor.findKey(editor, element);
  } catch (e: any) {
    logger.error('[getDeepLinkKey] Failed to get key for deep link element', e);
    return null;
  }
};
